"use client";

import Image from "next/image";
import { useBlogPostCommentsSection } from "@/hooks/customer/useBlogPostCommentsSection";

export default function BlogPostCommentsSection({ blogPublicId }) {
  const {
    getInitials,
    isValidGuestEmail,
    CommentBlock,
    isAuthenticated,
    comments,
    loadErr,
    draft,
    setDraft,
    loading,
    submitting,
    guestModalOpen,
    setGuestModalOpen,
    guestName,
    setGuestName,
    guestEmail,
    setGuestEmail,
    replyingToId,
    setReplyingToId,
    commentsExpanded,
    setCommentsExpanded,
    isAuthor,
    onRequireBlogWriterLogin,
    replyingPreview,
    composerRef,
    customerDisplayName,
    customerAvatar,
    onSubmitClick,
    confirmGuest,
    visibleThreads,
    hiddenCommentNodes,
    hasMoreComments,
    compactCommentsLayout,
    commentsListClassName,
  } = useBlogPostCommentsSection(blogPublicId);

  if (!blogPublicId) return null;

  return (
    <section
      className={compactCommentsLayout ? "mt-6" : "mt-10"}
      id="blog-comments"
    >
      <p className="text-sm text-black/60">We&apos;d love to hear from you</p>
      <div
        className={`font-bold text-black mt-0.5 ${compactCommentsLayout ? "mb-3" : "mb-4"}`}
      >
        Comments
      </div>

      <div
        className={
          compactCommentsLayout
            ? "rounded-2xl border border-gray-200 bg-white p-4 sm:p-4"
            : "rounded-2xl border border-gray-200 bg-white p-5 sm:p-6"
        }
      >
        {replyingToId ? (
          <div className="mb-3 rounded-md border border-[#284078]/15 bg-[#284078]/5 px-2 py-1.5">
            <p className="text-[10px] font-semibold text-[#284078]">
              You are replying to this comment{" "}
              <button
                type="button"
                className="underline font-medium cursor-pointer"
                onClick={() => setReplyingToId(null)}
              >
                Cancel
              </button>
            </p>
            {replyingPreview ? (
              <p className="mt-0.5 text-[10px] text-black/65 leading-snug border-l-2 border-[#FBBC05] pl-2 italic">
                &ldquo;{replyingPreview}&rdquo;
              </p>
            ) : null}
          </div>
        ) : null}

        <div ref={composerRef} className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-full overflow-hidden bg-[#FBBC05]/70 border border-black/10 shrink-0 flex items-center justify-center text-xs font-semibold text-black">
            {isAuthenticated && customerAvatar ? (
              <Image
                src={customerAvatar}
                alt="Avatar"
                width={32}
                height={32}
                className="h-full w-full object-cover"
                   
                />
            ) : (
              <span className="span-text">
                {isAuthenticated ? getInitials(customerDisplayName) : "KT"}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={compactCommentsLayout ? 1 : 2}
              placeholder={
                replyingToId
                  ? "Write your reply…"
                  : "Write your comment here..."
              }
              className={
                compactCommentsLayout
                  ? "w-full rounded-xl border border-black/15 bg-white px-3 py-1.5 text-sm text-black outline-none focus:border-[#FBBC05] resize-y min-h-[44px]"
                  : "w-full rounded-xl border border-black/15 bg-white px-3 py-1.5 text-sm text-black outline-none focus:border-[#FBBC05] resize-y min-h-[56px]"
              }
            />
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                disabled={submitting || !String(draft || "").trim()}
                onClick={onSubmitClick}
                className="rounded-lg bg-[#FBBC05] px-4 py-1.5 btn-text font-semibold text-black disabled:opacity-50 hover:opacity-95 cursor-pointer"
              >
                {submitting
                  ? "Posting…"
                  : replyingToId
                    ? "Post reply"
                    : "Post comment"}
              </button>
            </div>
          </div>
        </div>

        {loadErr ? (
          <p className="mt-4 text-sm text-red-600">{loadErr}</p>
        ) : null}

        <div className={commentsListClassName}>
          {loading ? (
            <p className="text-sm text-black/50">Loading comments…</p>
          ) : comments.length === 0 ? (
            <p className="text-sm text-black/50">
              No comments yet. Be the first to share your thoughts.
            </p>
          ) : (
            visibleThreads.map((c) => (
              <CommentBlock
                key={c.id}
                comment={c}
                isAuthor={isAuthor}
                replyingToId={replyingToId}
                onReplyClick={setReplyingToId}
                onRequireBlogWriterLogin={onRequireBlogWriterLogin}
                depth={0}
              />
            ))
          )}
        </div>

        {!loading && hasMoreComments ? (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => setCommentsExpanded((v) => !v)}
              className="rounded-full border border-gray-200 bg-white px-4 py-2 btn-text font-semibold text-black hover:bg-black/[0.02] cursor-pointer"
            >
              {commentsExpanded
                ? "Show less"
                : `See more (${hiddenCommentNodes} more)`}
            </button>
          </div>
        ) : null}
      </div>

      {guestModalOpen ? (
        <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40 cursor-pointer"
            aria-label="Close"
            onClick={() => {
              setGuestModalOpen(false);
            }}
          />
          <div className="relative w-full max-w-sm rounded-2xl bg-white border border-black/10 shadow-2xl p-5">
            <h3 className="font-semibold text-black">
              Post as guest
            </h3>
            <p className="mt-1 text-xs text-black/60">
              Please enter your name and email to post a comment.
            </p>
            <input
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="mt-3 w-full rounded-xl border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#FBBC05]"
              placeholder="e.g. Alex Kumar"
              maxLength={80}
            />
            <input
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              className="mt-2 w-full rounded-xl border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#FBBC05]"
              placeholder="e.g. alex@example.com"
              inputMode="email"
              autoComplete="email"
              maxLength={120}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-black/15 px-4 py-2 btn-text font-medium text-black/80 cursor-pointer"
                onClick={() => {
                  setGuestModalOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  !guestName.trim() ||
                  !guestEmail.trim() ||
                  !isValidGuestEmail(guestEmail)
                }
                onClick={confirmGuest}
                className="rounded-xl bg-[#FBBC05] px-4 py-2 btn-text font-semibold text-black disabled:opacity-50 cursor-pointer"
              >
                Post comment
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
